import fetcher, { HTTPMethods, uploadWithProgress } from "../hooks/fetcher";
import {
  STATUS,
  confirming,
  failed,
  failureOf,
  queued,
  started,
  withUploadPercent,
} from "../domain/mediaUpload";

const DEFAULT_FILENAME = "recording.webm";

/* A plain function rather than a hook so several uploads can be in flight at
   once; progress goes to the caller through `onEvent`. */
export const uploadMedia = (file, options = {}, { onEvent } = {}) => {
  const {
    uploadType,
    referenceId,
    influencerId,
    mirrorVideo = false,
    mimeType,
  } = options;

  const contentType = mimeType ?? file.type;
  const report = onEvent ?? (() => {});

  const aborter = { cancel: () => {} };

  const run = async () => {
    report(started());

    const { mediaUploadId, presignedUrl } = await fetcher(
      "/api/mediaUpload/init",
      {
        method: HTTPMethods.POST,
        body: JSON.stringify({
          uploadType,
          referenceId,
          influencerId,
          filename: file.name ?? DEFAULT_FILENAME,
          contentType,
          fileSize: file.size,
        }),
      },
    );

    report(withUploadPercent(0));

    const { controller, promise } = uploadWithProgress(presignedUrl, file, {
      method: HTTPMethods.PUT,
      headers: { "Content-Type": contentType },
      onProgress: (percent) => report(withUploadPercent(percent)),
    });

    aborter.cancel = () => controller.abort();

    await promise;

    aborter.cancel = () => {};
    report(confirming());

    await fetcher("/api/mediaUpload/confirm", {
      method: HTTPMethods.POST,
      body: JSON.stringify({ mediaUploadId, mirrorVideo, mimeType }),
    });

    report(queued(mediaUploadId));

    return { mediaUploadId };
  };

  const promise = run().catch((err) => {
    aborter.cancel = () => {};
    report(failed(failureOf(err)));
    throw err;
  });

  return {
    cancel: () => aborter.cancel(),
    promise,
  };
};

// The raw file is still in S3, so this costs a requeue rather than another
// upload of the same bytes.
export const retryMedia = (mediaUploadId) =>
  fetcher(`/api/mediaUpload/${mediaUploadId}/retry`, {
    method: HTTPMethods.POST,
  });

// Resolves null rather than throwing: a failed reconciliation should leave the
// client's own state alone.
export const fetchUploadStatus = async (mediaUploadId) => {
  try {
    return await fetcher(`/api/mediaUpload/${mediaUploadId}`);
  } catch {
    return null;
  }
};

export default { uploadMedia, retryMedia, fetchUploadStatus, STATUS };
